import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { api_auth } from "../../services/api_config";
import { jwtDecode } from "jwt-decode";
import InputField from "../InputField/InputField";
import { FcGoogle } from "react-icons/fc";
import { FaGithub } from "react-icons/fa";
import Divider from "@mui/material/Divider";
import Buttons from "../../utils/Buttons";
import toast from "react-hot-toast";
import { useMyContext } from "../../store/ContextApi";
import { useEffect } from "react";
import { encryptPassword } from "../../utils/encryptPassword";

const apiUrl = process.env.REACT_APP_AUTH_API_URL;

const oauthButtonClass = "auth-oauth-btn";

const Login = () => {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [jwtToken, setJwtToken] = useState("");
  const [loading, setLoading] = useState(false);
  const { setToken, token } = useMyContext();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { username: "", password: "", code: "" },
    mode: "onTouched",
  });

  const handleSuccessfulLogin = (token, decodedToken) => {
    const user = {
      username: decodedToken.sub,
      roles: decodedToken.roles ? decodedToken.roles.split(",") : [],
    };
    localStorage.setItem("USER", JSON.stringify(user));
    setToken(token);
    navigate("/");
  };

  const onCheckUsername = async (data) => {
    try {
      setLoading(true);
      const response = await api_auth.post("/auth/public/check-username", {
        username: data.username,
      });
      if (response.data.exists) {
        setUsername(data.username);
        setStep(2);
      } else {
        toast.error("Account not found");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const onLoginHandler = async (data) => {
    try {
      setLoading(true);
      const encryptedPassword = await encryptPassword(data.password);
      const response = await api_auth.post("/auth/public/signin", {
        username,
        password: encryptedPassword,
      });

      toast.success("Login Successful");
      reset();

      if (response.status === 200 && response.data.jwtToken) {
        setJwtToken(response.data.jwtToken);
        const decodedToken = jwtDecode(response.data.jwtToken);
        if (decodedToken.is2faEnabled) {
          setStep(3);
        } else {
          handleSuccessfulLogin(response.data.jwtToken, decodedToken);
        }
      } else {
        toast.error("Login failed. Please check your credentials and try again.");
      }
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const onVerify2FaHandler = async (data) => {
    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append("code", data.code);
      formData.append("jwtToken", jwtToken);
      await api_auth.post("/auth/public/verify-2fa-login", formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      const decodedToken = jwtDecode(jwtToken);
      handleSuccessfulLogin(jwtToken, decodedToken);
    } catch (error) {
      toast.error("Invalid 2FA code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) navigate("/");
  }, [navigate, token]);

  return (
    <div className="auth-page">
      {step === 1 ? (
        <form onSubmit={handleSubmit(onCheckUsername)} className="auth-card">
          <div>
            <h1 className="auth-title">Sign In</h1>
            <p className="auth-subtitle">Enter your username to continue</p>
            <div className="flex items-center justify-between gap-2 py-5">
              <a href={`${apiUrl}/oauth2/authorization/google`} className={oauthButtonClass}>
                <FcGoogle className="text-2xl" />
                <span className="hidden sm:inline">Google</span>
              </a>
              <a href={`${apiUrl}/oauth2/authorization/github`} className={oauthButtonClass}>
                <FaGithub className="text-2xl" />
                <span className="hidden sm:inline">GitHub</span>
              </a>
            </div>
            <Divider className="auth-divider font-semibold">OR</Divider>
          </div>
          <div className="flex flex-col gap-2 mt-4">
            <InputField
              label="UserName"
              required
              id="username"
              type="text"
              message="*UserName is required"
              placeholder="type your username"
              register={register}
              errors={errors}
            />
          </div>
          <Buttons
            disabled={loading}
            onClickhandler={() => {}}
            className="btn-primary w-full my-4"
            type="text"
          >
            {loading ? <span>Loading...</span> : "Next"}
          </Buttons>
          <p className="auth-text text-center mt-2">
            Don't have an account?{" "}
            <Link className="auth-link" to="/signup">SignUp</Link>
          </p>
        </form>
      ) : step === 2 ? (
        <form onSubmit={handleSubmit(onLoginHandler)} className="auth-card">
          <div>
            <h1 className="auth-title">Welcome, {username}</h1>
            <p className="auth-subtitle">Enter your password to sign in</p>
            <Divider className="auth-divider font-semibold pb-4 mt-4" />
          </div>
          <div className="flex flex-col gap-2 mt-4">
            <InputField
              label="Password"
              required
              id="password"
              type="password"
              message="*Password is required"
              placeholder="type your password"
              register={register}
              errors={errors}
            />
          </div>
          <Buttons
            disabled={loading}
            onClickhandler={() => {}}
            className="btn-primary w-full my-4"
            type="text"
          >
            {loading ? <span>Loading...</span> : "Sign In"}
          </Buttons>
          <div className="flex justify-between auth-text">
            <button
              type="button"
              onClick={() => { setStep(1); reset(); }}
              className="auth-link"
            >
              ← Back
            </button>
            <Link className="auth-link" to="/forgot-password">
              Forgot Password?
            </Link>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit(onVerify2FaHandler)} className="auth-card">
          <div>
            <h1 className="auth-title">Verify 2FA</h1>
            <p className="auth-subtitle">
              Enter the correct code to complete 2FA Authentication
            </p>
            <Divider className="auth-divider font-semibold pb-4 mt-4" />
          </div>
          <div className="flex flex-col gap-2 mt-4">
            <InputField
              label="Enter Code"
              required
              id="code"
              type="text"
              message="*Code is required"
              placeholder="Enter your 2FA code"
              register={register}
              errors={errors}
            />
          </div>
          <Buttons
            disabled={loading}
            onClickhandler={() => {}}
            className="btn-primary w-full my-4"
            type="text"
          >
            {loading ? <span>Loading...</span> : "Verify 2FA"}
          </Buttons>
        </form>
      )}
    </div>
  );
};

export default Login;
